// =============================================================
//  audioFile.js -- single-file audio playback engine
//
//  handleAudioCue, stopAllAudio
//  primeAudioOverlay, primeWaveform
//  Global stop button handler
//
//  Polyphony:
//    Default (no poly or poly:1) -> mono, retrigger = live-update
//    poly:0  -> unlimited overlapping voices
//    poly:N  -> up to N voices (oldest stopped when at limit)
//
//    Poly voices get unique registry keys, independent audio
//    graphs, and per-voice waveform peak layers + cursors.
// =============================================================

import "../../control/liveSignals.js";   // the level / hit / n of every sound, and the beat
import { set as busSet } from "../../control/paramBus.js";

// <uid>.end on the parameter bus: 0 as a sound starts, 1 when it has played
// to its end (all its loops; not when stopped). A map-mode station can wait
// for it — station(until:"<uid>.end") — and so can any trig: condition.
function noteAudioEnd(uid, v) {
  if (!uid) return;
  try { busSet(`${uid}.end`, v, { source: "audio" }); } catch { /* no bus */ }
}
import {
  renderWaveform,
  getWaveform,
  destroyWaveform,
  resizeWaveformsForElement,
  waveformsForElement,
  updatePeaks,
  startCursor,
  resetCursor,
  addCursor,
  removeCursor,
  addPeakLayer,
  removePeakLayer,
  updateWaveformLayout,
  enableCursorDrag,
  disableCursorDrag,
  getCursorParams
} from "../../system/waveform.js";
import { renderScope, startScope, resizeScope } from "../../system/scope.js";
import "./cursorRestore.js";
import { stopAllSynths, nukeSynthAudio, stopSynth, releaseHolds } from "../synth.js";
import { stopAudioImpulse, stopAllAudioImpulses } from "./audioImpulse.js";
import { registerTask } from "../../system/loadTasks.js";
import { syncLiveOverflow } from "../../system/liveOverflow.js";
import { resetAnimationsForElement } from "../animation.js";
import {
  sharedAudioCtx,
  masterOutput,
  getBus,
  evalMaybeRandom,
  createAudioOverlay,
  formatAudioFileOverlay,
  parseOverlayLevel,
  getReversedBuffer,
  resolveFade,
  buildAudioInfoLabel,
  buildAudioInfoHeader,
  buildAudioInfoDetails,
  getPlaybackClientsFromEl,
  fetchAudioBuffer,
  ensureAudioExtension,
  makePatternGenerator,
  audioBufferCache,
  takeFollows,
  steerFollows,
  activeAudioCues,
} from "./audioShared.js";
import { buildReverb, updateReverb, disconnectReverb } from "./reverb.js";
import { channelFor, resetLevels } from "./level.js";
import { isModulator, modSpec, attachModulator } from "./modulators.js";
import { isExternalEngine, startExternalTape } from "./externalTape.js";
import { secs, secsOf } from "../beats.js";
import { onReset, runReset } from "../../system/teardown.js";
import { sendTo } from "./fxBus.js";
import { stageBand } from "../../system/viewBand.js";
import { boxGrid } from "../../system/boxGrid.js";
import { setVuMode } from "./vuModes.js";
import { isRel, applyRel } from "../relative.js";
import { buildFilter, buildDelay, clamp } from "./effects.js";
import { transport } from "../../system/transportState.js";
import { spatialPanner, bindSpatial, hasSpatial, toBus } from "./speakers.js";

// in: is a number, a list, or a Pseq/Prand pattern. A list or pattern
// means MULTIPLE START POINTS: every loop iteration (and every retrigger)
// draws the next one — in:[2.5, 14, 31] cycles through them in order,
// in:Prand(2.5, 14, 31, inf) jumps between them at random. Returns the
// generator (null for a plain number) and the first start point.
function makeInSpec(raw) {
  let gen = null;
  if (raw && typeof raw === "object" && raw.type === "pattern") gen = makePatternGenerator(raw);
  else if (Array.isArray(raw)) gen = makePatternGenerator({ values: raw });
  if (gen) {
    const v = Number(evalMaybeRandom(gen.next()));
    return { gen, first: Number.isFinite(v) && v >= 0 ? v : 0 };
  }
  return { gen: null, first: Math.max(0, Number(evalMaybeRandom(raw)) || 0) };
}

// Advance a voice to its next start point (no-op for a plain number)
function drawNextIn(live) {
  if (!live.inGen) return;
  const v = Number(evalMaybeRandom(live.inGen.next()));
  if (Number.isFinite(v) && v >= 0) live.in = v;
}

// seg: is the length (s) of each play FROM its start point — a number or a
// list/pattern drawn per play. With in: as a pattern this is a grain /
// glitch-rhythm generator: seg:0.2 + in:Prand(...) = 200 ms grains jumping
// between start points. 0 = not set (play to out: or the file end).
function drawNextSeg(live) {
  if (!live.segGen) return;
  const v = Number(evalMaybeRandom(live.segGen.next()));
  if (Number.isFinite(v) && v > 0) live.seg = v;
}

// speed: as a pattern is stepped per TRIGGER (shared per-cue generator) and
// — for looping voices — per PLAY as well, so every grain of a glitch
// generator can have its own pitch; negative values play the grain
// backwards, so one pattern randomises pitch and direction together.
function drawNextSpeed(live) {
  if (!live.speedPattern) return;
  const v = nextSpeedPatternValue(live.speedKey, live.speedPattern);
  if (Number.isFinite(v) && v !== 0) live.speed = v;
}

// End point of the current play: seg: (from the start point) wins over
// out:, both clamped to the buffer; 0/unset = the file end.
function resolveEnd(live, inPoint) {
  const dur = live.buf.duration;
  if (live.seg > 0) return Math.min(dur, inPoint + live.seg);
  return (live.out > 0 && live.out <= dur) ? live.out : dur;
}

// =============================================================
//  AUDIO EFFECTS: the filter and delay are effects.js's, as a synth's
// =============================================================

/**
 * Wire the audio effects chain.
 * Returns the final output node to connect to destination.
 *
 * @param {AudioContext} ctx
 * @param {AudioNode} inputNode - The node to connect from (e.g., panNode or gainNode)
 * @param {object} effectParams - { filter, delay, reverb }
 * @returns {object} - { outputNode, filter, delay, reverb }
 */
function wireEffectsChain(ctx, inputNode, effectParams, dryDest = masterOutput) {
  const filter = buildFilter(ctx, effectParams.filter);
  const delay = buildDelay(ctx, effectParams.delay);
  const reverb = buildReverb(ctx, effectParams.reverb);

  // VOL controls _masterVolNode — the true master above all buses — so no
  // per-voice postFader is needed.
  let currentNode = inputNode;

  // Filter is inline (no dry/wet)
  if (filter) {
    currentNode.connect(filter);
    currentNode = filter;
  }

  // No effects with wet/dry? Connect directly to destination
  if (!delay && !reverb) {
    currentNode.connect(dryDest);
    return { outputNode: currentNode, filter, delay, reverb, dryDest };
  }

  // All paths (dry + wet) go to the same named bus so each bus
  // processes its own reverb/delay through its own bus chain.
  currentNode.connect(dryDest);

  if (delay) {
    currentNode.connect(delay.delay);
    delay.delay.connect(delay.wet);
    delay.wet.connect(dryDest);
  }
  if (reverb) {
    currentNode.connect(reverb.input);
    reverb.wet.connect(dryDest);
  }

  return { outputNode: currentNode, filter, delay, reverb, dryDest };
}

// =============================================================
//  LIVE AUDIO RECTS
//  Ephemeral SVG rects created when audio is triggered from the
//  live console (no cueElement). Anchors waveform + cursor display.
// =============================================================

const SVG_NS = "http://www.w3.org/2000/svg";
const LIVE_GROUP_ID = "oscilla-live-audio-group";
const liveAudioRects = new Map();   // uid -> rect element

// Per-cue speed pattern generators for DSL speed:Pseq(...)/Prand(...).
// Each triggered voice takes the next step; nested patterns and hrand()
// are handled by the shared generator.
const _speedPatternGens = new Map();
onReset("project", function forgetSpeedPatterns() { _speedPatternGens.clear(); });
function nextSpeedPatternValue(key, pattern) {
  const sig = JSON.stringify(pattern);
  let rec = _speedPatternGens.get(key);
  if (!rec || rec.sig !== sig) {
    rec = { sig, gen: makePatternGenerator(pattern) };
    _speedPatternGens.set(key, rec);
  }
  let v = rec.gen?.next();
  if (v == null) {
    rec.gen = makePatternGenerator(pattern);
    v = rec.gen?.next();
  }
  const num = evalMaybeRandom(v);
  if (typeof num === "number" && Number.isFinite(num)) return num;
  return (typeof v === "number" && Number.isFinite(v)) ? v : 1;
}

const LIVE_COLORS = [
  "#07a", "#c00", "#2a7", "#e80", "#80c", "#089",
  "#a60", "#c55", "#5a3", "#a3c", "#c80", "#369"
];
let liveColorIndex = 0;

/** Return the next cycling color from the shared live palette. */
export function nextLiveColor() {
  const c = LIVE_COLORS[liveColorIndex % LIVE_COLORS.length];
  liveColorIndex++;
  return c;
}

function getOrCreateLiveGroup(svg) {
  let g = svg.getElementById(LIVE_GROUP_ID);
  if (!g) {
    g = document.createElementNS(SVG_NS, "g");
    g.id = LIVE_GROUP_ID;
    svg.appendChild(g);
  }
  return g;
}

/**
 * Create (or reuse) an ephemeral SVG rect. Until viz() places it or it is
 * dragged, it is laid out with the others nothing has placed: a grid over
 * the stage, all on the screen (layoutLiveBoxes).
 */
export function createLiveAudioRect(svg, uid) {
  if (liveAudioRects.has(uid)) return liveAudioRects.get(uid);

  const g = getOrCreateLiveGroup(svg);

  // Assign a cycling color for waveform polylines
  const color = nextLiveColor();

  // Screen center -> SVG coordinates
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const inv = ctm.inverse();
  const pt = svg.createSVGPoint();
  pt.x = window.innerWidth / 2;
  pt.y = window.innerHeight / 2;
  const center = pt.matrixTransform(inv);

  const RECT_W = BOX_W;
  const RECT_H = BOX_H;
  // in a score: stacked down from the middle of the view, and there it
  // stays, scrolling with the score (Live's stage lays them out: layoutLiveBoxes)
  const stackIndex = onLiveStage() ? 0 : liveAudioRects.size;

  const rect = document.createElementNS(SVG_NS, "rect");
  rect.id = uid;
  rect._autoPlaced = true;
  rect.setAttribute("x", center.x - RECT_W / 2);
  rect.setAttribute("y", center.y - RECT_H / 2 + stackIndex * (RECT_H + BOX_GAP));
  rect.setAttribute("width", RECT_W);
  rect.setAttribute("height", RECT_H);
  // transparent, not none: a fill is what catches the drag (pointer-events:
  // fill); a faint white would show as a grey box on a black stage
  rect.setAttribute("fill", "transparent");
  rect.setAttribute("stroke", "none");           // no frame: viz(uid, border:) adds one
  rect.setAttribute("stroke-width", "0.5");
  rect.setAttribute("pointer-events", "fill");
  rect.style.cursor = "grab";
  rect.dataset.liveAudio = uid;
  rect._waveformColor = color;

  // -------------------------------------------
  // DRAG -- uses translate transform so the waveform group
  // follows automatically via the MutationObserver in waveform.js
  // that mirrors the target element's transform attribute.
  // -------------------------------------------
  {
    const MOVE_THRESHOLD = 4;
    let moved = false;
    let startClientX = 0;
    let startClientY = 0;
    let startTX = 0;
    let startTY = 0;

    function screenToSVGDelta(dx, dy) {
      const m = svg.getScreenCTM();
      if (!m) return { dx: 0, dy: 0 };
      return { dx: dx / m.a, dy: dy / m.d };
    }

    function getTranslate() {
      const t = rect.getAttribute("transform") || "";
      const m = t.match(/translate\(\s*([-\d.]+)[\s,]+([-\d.]+)\s*\)/);
      return m ? { x: parseFloat(m[1]), y: parseFloat(m[2]) } : { x: 0, y: 0 };
    }

    function onPointerMove(e) {
      const dxPx = e.clientX - startClientX;
      const dyPx = e.clientY - startClientY;

      if (!moved) {
        if (Math.abs(dxPx) < MOVE_THRESHOLD &&
            Math.abs(dyPx) < MOVE_THRESHOLD) return;
        moved = true;
        rect.style.cursor = "grabbing";
      }

      const d = screenToSVGDelta(dxPx, dyPx);
      const t = `translate(${startTX + d.dx}, ${startTY + d.dy})`;
      rect.setAttribute("transform", t);
      // Sync resize handles
      for (const h of rect._edgeHandles || []) h.setAttribute("transform", t);
      // turning while it moves: about its middle where it is now, each move --
      // set only once let go, it swung round where it had been picked up
      syncBoxPivot(rect);
    }

    function onPointerUp() {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      rect.style.cursor = "grab";
      document.documentElement.classList.remove("oscilla-dragging");
      syncBoxPivot(rect);          // dragged while turning: turn where it is now
      if (moved) { rect._autoPlaced = false; rememberPlace(rect, false); layoutLiveBoxes(svg); mirrorBoxPlace(rect); }
    }

    // a press on the box -- or, with viz(uid, drag:1), anywhere on what it
    // draws, its scope over it (shape.js pressOnStage calls this)
    rect._dragStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // no pointer while it moves: the box is where the hand is (as a shape's, drag:1)
      if (rect.dataset.drag) document.documentElement.classList.add("oscilla-dragging");
      moved = false;
      startClientX = e.clientX;
      startClientY = e.clientY;
      const t = getTranslate();
      startTX = t.x;
      startTY = t.y;

      rect.style.cursor = "grabbing";
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    };
    rect.addEventListener("pointerdown", rect._dragStart);
  }

  // -------------------------------------------
  // EDGE RESIZE HANDLES
  // -------------------------------------------
  {
    const HANDLE_SIZE = 20; // wide grab zone, centered on edge
    const HALF = HANDLE_SIZE / 2;
    const MIN_WIDTH = 60;
    const MIN_HEIGHT = 40;
    const x = parseFloat(rect.getAttribute("x"));
    const y = parseFloat(rect.getAttribute("y"));
    const width = RECT_W;
    const height = RECT_H;

    function screenToSVG(clientX, clientY) {
      const ctm = svg.getScreenCTM();
      if (!ctm) return { x: 0, y: 0 };
      const inv = ctm.inverse();
      return {
        x: inv.a * clientX + inv.c * clientY + inv.e,
        y: inv.b * clientX + inv.d * clientY + inv.f,
      };
    }

    function makeEdgeHandle(attrs) {
      const h = document.createElementNS(SVG_NS, "rect");
      h.setAttribute("x", attrs.x);
      h.setAttribute("y", attrs.y);
      h.setAttribute("width", attrs.w);
      h.setAttribute("height", attrs.h);
      h.setAttribute("fill", "transparent");
      h.setAttribute("stroke", "none");
      h.setAttribute("pointer-events", "all");
      h.style.cursor = attrs.cursor;
      return h;
    }

    // After any resize, refresh the display to fit new dimensions.
    // Detects whether this is an audio waveform or synth scope
    // and calls the appropriate renderer.
    function refreshWaveformAfterResize() {
      // Resize ALL waveforms attached to this rect (including
      // waveform:uid routing from other cues)
      resizeWaveformsForElement(rect);

      // Scopes need destroy+rebuild
      const handle = getWaveform(uid);
      if (handle?._scopeLine) {
        const info = handle._infoText?.textContent || "";
        const wave = handle._scopeWave || null;
        const col = handle._scopeColor;
        const analyser = handle._scopeAnalyser;
        const wasRunning = handle._scopeRunning;
        destroyWaveform(uid);
        const newHandle = renderScope(svg, "self", uid, {
          element: rect,
          info,
          wave,
          color: col
        });
        if (newHandle && wasRunning && analyser) {
          startScope(newHandle, analyser);
        }
      }
    }

    // --- Right edge (horizontal resize) ---
    const rightHandle = makeEdgeHandle({
      x: x + width - HALF, y, w: HANDLE_SIZE, h: height,
      cursor: "ew-resize"
    });
    function onRightMove(e) {
      const world = screenToSVG(e.clientX, e.clientY);
      // Compensate for transform offset
      const t = rect.getAttribute("transform") || "";
      const tm = t.match(/translate\(\s*([-\d.]+)/);
      const tx = tm ? parseFloat(tm[1]) : 0;
      const rx = parseFloat(rect.getAttribute("x"));
      const newW = Math.max(MIN_WIDTH, world.x - tx - rx);
      rect.setAttribute("width", newW);
      rightHandle.setAttribute("x", rx + newW - HALF);
      bottomHandle.setAttribute("width", newW);
      topHandle.setAttribute("width", newW);
    }
    function onRightUp() {
      window.removeEventListener("pointermove", onRightMove);
      window.removeEventListener("pointerup", onRightUp);
      rect._autoPlaced = false;           // sized by hand: it stays so, the others close up
      rememberPlace(rect, true);
      layoutLiveBoxes(svg);
      refreshWaveformAfterResize();
    }
    rightHandle.addEventListener("pointerdown", (e) => {
      e.preventDefault(); e.stopPropagation();
      window.addEventListener("pointermove", onRightMove);
      window.addEventListener("pointerup", onRightUp);
    });

    // --- Bottom edge (vertical resize downward) ---
    const bottomHandle = makeEdgeHandle({
      x, y: y + height - HALF, w: width, h: HANDLE_SIZE,
      cursor: "ns-resize"
    });
    function onBottomMove(e) {
      const world = screenToSVG(e.clientX, e.clientY);
      const t = rect.getAttribute("transform") || "";
      const tm = t.match(/translate\([^,]+,\s*([-\d.]+)/);
      const ty = tm ? parseFloat(tm[1]) : 0;
      const ry = parseFloat(rect.getAttribute("y"));
      const newH = Math.max(MIN_HEIGHT, world.y - ty - ry);
      rect.setAttribute("height", newH);
      bottomHandle.setAttribute("y", ry + newH - HALF);
      rightHandle.setAttribute("height", newH);
    }
    function onBottomUp() {
      window.removeEventListener("pointermove", onBottomMove);
      window.removeEventListener("pointerup", onBottomUp);
      rect._autoPlaced = false;
      rememberPlace(rect, true);
      layoutLiveBoxes(svg);
      refreshWaveformAfterResize();
    }
    bottomHandle.addEventListener("pointerdown", (e) => {
      e.preventDefault(); e.stopPropagation();
      window.addEventListener("pointermove", onBottomMove);
      window.addEventListener("pointerup", onBottomUp);
    });

    // --- Top edge (vertical resize upward) ---
    const topHandle = makeEdgeHandle({
      x, y: y - HALF, w: width, h: HANDLE_SIZE,
      cursor: "ns-resize"
    });
    let _topStartY = 0;
    let _topStartRectY = 0;
    let _topStartH = 0;

    function onTopMove(e) {
      const world = screenToSVG(e.clientX, e.clientY);
      const t = rect.getAttribute("transform") || "";
      const tm = t.match(/translate\([^,]+,\s*([-\d.]+)/);
      const ty = tm ? parseFloat(tm[1]) : 0;
      const delta = (world.y - ty) - _topStartY;
      const newY = _topStartRectY + delta;
      const newH = Math.max(MIN_HEIGHT, _topStartH - delta);

      if (newH > MIN_HEIGHT) {
        rect.setAttribute("y", newY);
        rect.setAttribute("height", newH);
        topHandle.setAttribute("y", newY - HALF);
        bottomHandle.setAttribute("y", newY + newH - HALF);
        rightHandle.setAttribute("y", newY);
        rightHandle.setAttribute("height", newH);
      }
    }
    function onTopUp() {
      window.removeEventListener("pointermove", onTopMove);
      window.removeEventListener("pointerup", onTopUp);
      rect._autoPlaced = false;
      rememberPlace(rect, true);
      layoutLiveBoxes(svg);
      refreshWaveformAfterResize();
    }
    topHandle.addEventListener("pointerdown", (e) => {
      e.preventDefault(); e.stopPropagation();
      _topStartY = screenToSVG(e.clientX, e.clientY).y;
      const t = rect.getAttribute("transform") || "";
      const tm = t.match(/translate\([^,]+,\s*([-\d.]+)/);
      _topStartY -= tm ? parseFloat(tm[1]) : 0;
      _topStartRectY = parseFloat(rect.getAttribute("y"));
      _topStartH = parseFloat(rect.getAttribute("height"));
      window.addEventListener("pointermove", onTopMove);
      window.addEventListener("pointerup", onTopUp);
    });

    rect._edgeHandles = [rightHandle, bottomHandle, topHandle];

    // Sync initial transform (in case rect was dragged before handles existed)
    const curT = rect.getAttribute("transform") || "";
    for (const h of rect._edgeHandles) {
      h.setAttribute("transform", curT);
      g.appendChild(h);
    }
  }

  g.appendChild(rect);
  liveAudioRects.set(uid, rect);
  // drawn beside the box, moved with it: s3 > rotate(...) turns the trace too
  rect._carry = () => [...waveformsForElement(rect).map(h => h.group), ...(rect._edgeHandles || [])];
  syncBoxPivot(rect);
  layoutLiveBoxes(svg);
  syncLiveOverflow(svg);   // centred on screen: maybe left of where the page begins
  return rect;
}

// a box at full size (the score's units), and the room between boxes (px)
const BOX_W = 400, BOX_H = 120, BOX_GAP = 20, BOX_MARGIN = 24;

/** Oscilla Live's stage: the window's own (live.js), not a score's, which scrolls. */
const onLiveStage = () => !!document.querySelector("#scrollStage[data-live-stage]");

/** The frame a box is placed in: its parent, past any wrapper an animation put round it. */
function boxFrame(rect) {
  let n = rect.parentNode;
  while (n && [...(n.classList || [])].some(c => c.startsWith("oscilla-anim"))) n = n.parentNode;
  return n;
}

/**
 * The boxes nothing has placed, laid out together over the stage
 * (boxGrid.js): a column down the middle, then more columns, smaller only
 * once they do not fit. Run as one comes or goes, and as the window resizes.
 */
function layoutLiveBoxes(svg) {
  if (!onLiveStage()) return;
  const auto = [...liveAudioRects.values()].filter(r => r._autoPlaced && r.isConnected && r.ownerSVGElement === svg);
  if (!auto.length) return;
  const frameCtm = boxFrame(auto[0])?.getScreenCTM();
  if (!frameCtm) return;
  const unit = Math.hypot(frameCtm.a, frameCtm.b) || 1;     // px per unit
  const band = stageBand();
  const area = { width: band.width - 2 * BOX_MARGIN, height: band.height - 2 * BOX_MARGIN };
  const { cells } = boxGrid(auto.length, area, { w: BOX_W * unit, h: BOX_H * unit, gap: BOX_GAP });
  auto.forEach((rect, i) => {
    const inv = boxFrame(rect)?.getScreenCTM()?.inverse();
    if (!inv) return;
    const c = cells[i];
    const left = band.left + BOX_MARGIN + c.x, top = band.top + BOX_MARGIN + c.y;
    const p0 = new DOMPoint(left, top).matrixTransform(inv);
    const p1 = new DOMPoint(left + c.w, top + c.h).matrixTransform(inv);
    placeLiveRect(rect, { x: p0.x, y: p0.y, w: p1.x - p0.x, h: p1.y - p0.y });
    rect._autoPlaced = true;
  });
}

let _layoutTimer = null;
if (typeof window !== "undefined") {
  window.addEventListener("resize", () => {
    clearTimeout(_layoutTimer);
    _layoutTimer = setTimeout(() => {
      for (const svg of new Set([...liveAudioRects.values()].map(r => r.ownerSVGElement).filter(Boolean))) layoutLiveBoxes(svg);
    }, 150);
  });
}

/**
 * Remove a live audio rect and its waveform.
 * Called when explicitly clearing a layer, or from stopAllAudio.
 */
export function removeLiveAudioRect(uid) {
  const rect = liveAudioRects.get(uid);
  if (rect) {
    // Remove edge resize handles
    if (rect._edgeHandles) {
      for (const h of rect._edgeHandles) h.remove();
    }
    const svg = rect.ownerSVGElement;
    // everything drawn into it goes with it: a poly synth's voices each
    // draw their own scope (scope-s3__0, __1 ...) into the one box
    for (const handle of waveformsForElement(rect)) destroyWaveform(handle.uid);
    // an animation that turned it, and the wrapper it turned it in
    resetAnimationsForElement(rect);
    const reg = window.oscillaAnimRegistry || {};
    for (const [k, entry] of Object.entries(reg)) if (entry?.el === rect) delete reg[k];
    const wraps = animWrappersOf(rect);
    rect.remove();
    for (const w of wraps) if (!w.children.length) w.remove();
    liveAudioRects.delete(uid);
    if (svg) layoutLiveBoxes(svg);      // the others close up
    syncLiveOverflow(svg);
  }
  destroyWaveform(uid);
}

/**
 * Remove all live audio rects (e.g. on score reload).
 */
export function clearLiveAudioRects() {
  for (const [uid, rect] of liveAudioRects) {
    if (rect._keepOnHush) continue;     // vu()'s meters: silence is worth seeing
    removeLiveAudioRect(uid);
  }
}

/**
 * Move and size a live rect (in its own SVG units) as its edge handles
 * would, and refit what is drawn in it: waveforms resize, scopes relay out.
 * Used by viz(uid, at:, size:, full:).
 */
/**
 * Where a dragged box is now, as viz(uid, at:[x, y]) -- its middle, 0..1 of
 * the band -- for the projector window to run (oscillaLive.js mirrorRun):
 * it moves there too. Only the latest for each box is kept to replay.
 */
/**
 * Where a box put by hand is, as viz() keeps it (rect._place, viz.js):
 * its middle as shares of the stage -- and, sized by hand, its size -- so
 * a resized window puts it back there.
 */
function rememberPlace(rect, withSize) {
  const r = rect.getBoundingClientRect();
  const b = stageBand();
  rect._place = {
    at: [(r.left + r.width / 2 - b.left) / (b.width || 1), (r.top + r.height / 2 - b.top) / (b.height || 1)],
    size: withSize ? [r.width / (b.width || 1), r.height / (b.height || 1)] : rect._place?.size ?? null,
  };
}

function mirrorBoxPlace(rect) {
  const r = rect.getBoundingClientRect();
  const b = stageBand();
  const x = (r.left + r.width / 2 - b.left) / (b.width || 1);
  const y = (r.top + r.height / 2 - b.top) / (b.height || 1);
  const uid = rect.id.replace(/^scope-/, "");
  const expr = `viz(${uid}, at:[${+x.toFixed(4)}, ${+y.toFixed(4)}])`;
  window.dispatchEvent(new CustomEvent("oscilla:live-mirror", { detail: { expr, key: `drag:${rect.id}` } }));
}

export function placeLiveRect(rect, { x, y, w, h }) {
  const HALF = 10;                     // half the edge handles' grab zone
  rect._autoPlaced = false;            // placed: out of the grid (layoutLiveBoxes puts it back)
  rect.removeAttribute("transform");
  rect.setAttribute("x", x); rect.setAttribute("y", y);
  rect.setAttribute("width", w); rect.setAttribute("height", h);
  const [right, bottom, top] = rect._edgeHandles || [];
  const put = (el, a) => { if (!el) return; el.removeAttribute("transform"); for (const k in a) el.setAttribute(k, a[k]); };
  put(right, { x: x + w - HALF, y, height: h });
  put(bottom, { x, y: y + h - HALF, width: w });
  put(top, { x, y: y - HALF, width: w });
  resizeWaveformsForElement(rect);
  for (const handle of waveformsForElement(rect)) {
    if (handle._scopeLine) resizeScope(handle, rect);
  }
  syncBoxPivot(rect);
}

// =============================================================
//  LIVE VIEWS OF AUDIO
//  A box drawn as the file's waveform by default can be drawn live
//  instead -- viz(a1, view:spectrum | polar | phase):
//  each voice in it gets a scope (view-<voiceKey>) fed from its own
//  analyser, and the file's peaks step aside until view:wave.
// =============================================================

const isLiveView = v => !!v && v !== "wave";

/** Voices drawing into this box: the box's uid, or uid__… for stacked ones. */
function voicesIn(rect) {
  const reg = activeAudioCues;
  if (!reg || !rect) return [];
  return [...reg.entries()].filter(([, v]) => v._box === rect).map(([k]) => k);
}

function attachAudioView(voiceKey) {
  const voice = activeAudioCues.get(voiceKey);
  const rect = voice?._box;
  if (!rect?.dataset || !isLiveView(rect.dataset.vizView) || !voice.analyser) return;
  const uid = `view-${voiceKey}`;
  if (getWaveform(uid)) return;
  const svg = rect.ownerSVGElement;
  const handle = renderScope(svg, rect, uid, { element: rect, color: rect._waveformColor || "#0ac" });
  if (handle) startScope(handle, voice.analyser);
  showFilePeaks(rect, false);
}

/** The file's own drawing (peaks, cursors) in a box, shown or hidden. */
function showFilePeaks(rect, on) {
  for (const h of waveformsForElement(rect)) {
    if (h._scopeLine) continue;                       // a live view, not the file
    if (h.group) h.group.style.display = on ? "" : "none";
  }
}

/**
 * viz(a1, view:...) on an audio box: give every voice in it a live view,
 * or take them away and show the file again.
 */
export function syncAudioViews(rect) {
  if (isLiveView(rect.dataset.vizView)) {
    for (const k of voicesIn(rect)) attachAudioView(k);
    showFilePeaks(rect, false);
  } else {
    for (const h of waveformsForElement(rect)) if (h.uid?.startsWith("view-")) destroyWaveform(h.uid);
    showFilePeaks(rect, true);
  }
}

// a voice that ends takes its live view with it
window.addEventListener("oscilla:audio", (e) => {
  if (e.detail?.state === "stop" && e.detail.uid) destroyWaveform(`view-${e.detail.uid}`);
});

/** The wrappers an animation put around a box (rotate nests two). */
function animWrappersOf(rect) {
  const out = [];
  for (let n = rect.parentNode; n && [...(n.classList || [])].some(c => c.startsWith("oscilla-anim")); n = n.parentNode) out.push(n);
  return out;
}

/**
 * A box turns (s3 > rotate(...)) about its own middle. Its wrapper also
 * holds the scope's extent line, which runs far off to one side, so the
 * wrapper's bbox centre is not the box's: give rotate the point
 * (data-pivot), and move a turning wrapper's origin when the box moves.
 */
function syncBoxPivot(rect) {
  const t = (rect.getAttribute("transform") || "").match(/translate\(\s*([-\d.e]+)[\s,]+([-\d.e]+)/);
  const cx = Number(rect.getAttribute("x")) + Number(rect.getAttribute("width")) / 2 + (t ? Number(t[1]) : 0);
  const cy = Number(rect.getAttribute("y")) + Number(rect.getAttribute("height")) / 2 + (t ? Number(t[2]) : 0);
  rect.dataset.pivot = `${cx},${cy}`;
  for (const w of animWrappersOf(rect)) {
    w.dataset.pivot = rect.dataset.pivot;
    if (w.style.transformOrigin) w.style.transformOrigin = `${cx}px ${cy}px`;
  }
}

/**
 * A pool's or an impulse's box when it runs from the console, with no
 * element of its own in a score: named by its uid, as audio()'s and
 * synth()'s are, its waveforms drawn into it by `prime` (primePoolWaveform,
 * primeImpulseWaveform). Its voices are played with _box: it, so
 * viz(p1, view:...) draws them. null with waveform: anything but self.
 */
export async function consoleBox(ast, uid, prime) {
  const params = ast.params || ast;
  if ((params.waveform ?? "self") !== "self") return null;
  const svg = document.querySelector("#scoreInner svg") || document.querySelector(".oscilla-score-inner svg") || document.querySelector("svg");
  const box = svg ? createLiveAudioRect(svg, uid) : null;
  if (box) await prime(ast, box);
  return box;
}

// Console access
window.removeLiveAudioRect = removeLiveAudioRect;
window.clearLiveAudioRects = clearLiveAudioRects;

// =============================================================
//  handleAudioCue -- single file playback with Web Audio API
// =============================================================

/**
 * Where a tape started by the playhead should be at local time `now`: its
 * start point plus how far the score's time has gone past the element's
 * left edge (the transport maps x to time as x / scoreWidth × duration).
 *
 * The score's time is THIS window's playhead -- what the performer sees,
 * and what the tape's cursor must sit on. It is set once a frame (at
 * lastAnimationFrameTime), so it is carried on to `now` at the transport's
 * speed: read later in the frame unadjusted it put the tape a steady few
 * ms ahead. (The shared server transport was tried instead: after a pause,
 * a seek and play it can differ from the local playhead for seconds while
 * the playhead eases towards it, and the tape followed it away from the
 * line.)
 */
function scoreLock(el, startSec, rate) {
  const svg = el.ownerSVGElement;
  const leftWorld = () => {
    const s = svg?.getBoundingClientRect(), r = el.getBoundingClientRect();
    return s && s.width > 0 && transport.scoreWidth > 0 ? (r.left - s.left) / (s.width / transport.scoreWidth) : null;
  };
  const left = leftWorld();
  return {
    expected(now = performance.now()) {
      const durS = (Number(transport.duration) || 0) / 1000;
      if (left == null || !(transport.scoreWidth > 0) || !(durS > 0)) return null;
      const leftSec = (left / transport.scoreWidth) * durS;
      const since = transport.isPlaying && Number.isFinite(window.lastAnimationFrameTime)
        ? Math.min(0.05, Math.max(0, (now - window.lastAnimationFrameTime) / 1000)) : 0;
      const scoreSec = (transport.playheadX / transport.scoreWidth) * durS + since * (Number(transport.speedMultiplier) || 1);
      return startSec + (scoreSec - leftSec) * rate;
    },
    speed: () => Number(transport.speedMultiplier) || 1,
    playing: () => !!transport.isPlaying,
  };
}

export async function handleAudioCue(ast, cueElement = null) {
  const ctx = sharedAudioCtx;

  try {
    if (ctx.state === "suspended") await ctx.resume();

    let {
      src,
      uid,
      amp = 1,
      loop = 1,
      toggle = false,
      fade,
      fadeIn = 0,
      fadeOut = 0,
      pan,
      params = {}
    } = ast || {};

    // amp:*0.5, speed:*0.9, pan:+=0.2 -- from the voice of this name playing
    // now, or from 1 / 1 / 0 (relative.js)
    {
      const now = activeAudioCues.get(String(uid ?? params.uid ?? ""))?._live;
      const rel = (v, k, d) => (isRel(v) ? applyRel(now?.[k] ?? d, v) : v);
      amp = rel(amp, "amp", 1);
      pan = rel(pan, "pan", 0);
      params = { ...params, amp: rel(params.amp, "amp", 1), pan: rel(params.pan, "pan", 0), speed: rel(params.speed, "speed", 1) };
      if (isRel(ast.speed)) ast = { ...ast, speed: applyRel(now?.speed ?? 1, ast.speed) };
      for (const k of ["amp", "pan", "speed"]) if (params[k] === undefined) delete params[k];
    }

    // OVERLAY -- check for overlay flag
    const overlayFlag = Number(ast.overlay ?? params.overlay ?? 0);

    // SPEED (playback rate) -- prefer "speed", fall back to legacy "pitch"
    const writtenSpeed = ast.speed !== undefined ? ast.speed
      : ast.pitch !== undefined ? ast.pitch
      : (params.speed ?? params.pitch);

    // amp:, pan:, speed: may follow() a signal: the voice starts from its
    // value now and is steered by it from then on (steer, below)
    const follows = takeFollows({ amp, pan: pan ?? params.pan, speed: writtenSpeed });
    if (follows.amp) amp = follows.amp.now;
    if (follows.pan) pan = follows.pan.now;
    const rawSpeed = follows.speed ? follows.speed.now : writtenSpeed;
    let speed;
    if (rawSpeed && typeof rawSpeed === "object" && rawSpeed.type === "pattern") {
      // DSL pattern: each triggered voice takes the next pattern step
      const genKey = String(ast.uid ?? params.uid ?? ast.src ?? params.src ?? "audio");
      speed = nextSpeedPatternValue(genKey, rawSpeed);
    } else {
      speed = evalMaybeRandom(rawSpeed) ?? 1;
    }

    // amp:sinOsc(..., lo, hi) / pan:sinOsc(...) and family (modulators.js):
    // the gain peaks at hi and a tremolo after it does the moving; pan
    // starts at 0 and the modulator moves it -- each voice its own
    const ampSpec = isModulator(amp) ? modSpec(amp) : null;
    const ampTop = ampSpec ? Math.max(ampSpec.lo, ampSpec.hi, 1e-6) : 0;
    if (ampSpec) amp = ampTop;
    const panMod = isModulator(pan ?? params.pan) ? (pan ?? params.pan) : null;

    // PAN -- prefer direct, fallback to params
    let panVal = null;

    if (pan !== undefined) {
      panVal = evalMaybeRandom(pan);
    } else if (params.pan !== undefined) {
      panVal = evalMaybeRandom(params.pan);
    }

    if (panMod) panVal = 0;
    if (panVal !== null && panVal !== undefined) {
      panVal = Math.max(-1, Math.min(1, Number(panVal)));
    }

    const key = uid && uid.trim();
    if (!key) { console.warn("[cueAudio] Missing uid:", ast); return; }
    // bus:room -- through a shared fx bus, every voice of it (fxBus.js); bus:none its own way
    const busName = ast.bus ?? params.bus;
    if (busName != null) sendTo(key, busName);
    // vu:stereo / vu:mono -- how its meter shows it (vuModes.js)
    if ((ast.vu ?? params.vu) != null) setVuMode(key, ast.vu ?? params.vu);
    if (!src) { console.warn("[cueAudio] Missing src:", ast); return; }

    // ---------------------------------------------------------
    // POLYPHONY
    //
    // Default (no poly or poly:1): mono -- retrigger updates the
    // existing voice in place (live-update path).
    //
    // poly:0  -> unlimited overlapping voices
    // poly:N  -> up to N overlapping voices (oldest stopped)
    //
    // When poly is active, each trigger gets a unique registry
    // key so it never hits the toggle/live-update paths.
    // ---------------------------------------------------------
    const polyRaw = ast.poly ?? params.poly;
    const polyNum = polyRaw != null ? Number(polyRaw) : 1;
    const polyActive = polyNum !== 1;     // 0 = unlimited, >1 = capped
    const polyLimit = polyNum === 0 ? Infinity : polyNum;

    // ---------------------------------------------------------
    // POLY SPREAD: on playhead trigger with poly:N (N>1),
    // launch voices with staggered entry, equally-spaced offsets.
    // Voice 0 starts immediately, voice 1 after duration/N seconds, etc.
    // Each voice loops infinitely from its offset position.
    // Click triggers keep the original one-voice-per-click behavior.
    // ---------------------------------------------------------
    if (polyNum > 1 && !cueElement?._clickTriggered && !ast._isSpreadVoice) {
      const spreadFilename = ensureAudioExtension(src);
      const spreadBuf = await fetchAudioBuffer(ctx, spreadFilename);
      const duration = spreadBuf.duration;
      const staggerMs = (duration / polyNum) * 1000;
      // Increment generation so any cancelled generation's timeouts self-abort
      const gen = ((cueElement?._spreadGen || 0) + 1);
      if (cueElement) cueElement._spreadGen = gen;
      for (let i = 0; i < polyNum; i++) {
        const spreadAst = {
          ...ast,
          poly: 1,
          _isSpreadVoice: true,
        };
        if (i === 0) {
          handleAudioCue(spreadAst, cueElement);
        } else {
          // kept by the sound's name: hush() and stopSound cancel the voices not started yet
          const timers = spreadTimers.get(key) ?? spreadTimers.set(key, new Set()).get(key);
          const t = setTimeout(() => {
            timers.delete(t);
            // Bail if the cue was cancelled (playhead exited) before this voice started
            if (cueElement && cueElement._spreadGen !== gen) return;
            handleAudioCue(spreadAst, cueElement);
          }, staggerMs * i);
          timers.add(t);
        }
      }
      return;
    }

    // In poly mode or spread voice: unique key per trigger, skip toggle/retrigger
    let voiceKey = key;
    if (ast._isSpreadVoice) {
      voiceKey = `${key}__spread_${Date.now()}_${Math.floor(Math.random() * 9999)}`;
    } else if (polyActive) {
      voiceKey = `${key}__${Date.now()}_${Math.floor(Math.random() * 9999)}`;

      // Enforce poly limit: stop oldest voice when at capacity
      const reg = activeAudioCues;
      if (polyLimit < Infinity) {
        const activeKeys = [];
        for (const k of reg.keys()) {
          if (k === key || k.startsWith(`${key}__`)) {
            activeKeys.push(k);
          }
        }
        while (activeKeys.length >= polyLimit) {
          const oldest = activeKeys.shift();
          const voice = reg.get(oldest);
          try { voice?.stop?.(0.03); } catch (err) { console.warn("[AUDIO] voice stop failed:", err); }
          reg.delete(oldest);
        }
      }
    }

    const filename = ensureAudioExtension(src);

    // Keep raw values -- resolveFade handles percentages and rand() funcCalls
    if (fade !== undefined) {
      fadeIn = fade;
      fadeOut = fade;
    }
    fadeIn = fadeIn ?? 0;
    fadeOut = fadeOut ?? 0;
    // beats (beats.js) -- a "50%" of the file is left as it is
    fadeIn = secs(fadeIn);
    fadeOut = secs(fadeOut);

    const reg = activeAudioCues;

    // ---------------------------------------------------------
    // MONO MODE: toggle and live-update paths
    // Skipped entirely in poly mode (voiceKey is always unique)
    // ---------------------------------------------------------
    if (!polyActive) {
      // toggle handling
      if (toggle && reg.has(voiceKey)) {
        const v = reg.get(voiceKey);
        try { v?.stop?.(fadeOut); } catch (err) { console.warn("[AUDIO] voice stop failed:", err); }
        reg.delete(voiceKey);
        if (!v?.announcesStop) window.dispatchEvent(new CustomEvent("oscilla:audio", {
          detail: { uid: voiceKey, file: filename, state: "stop" }
        }));
        return;
      }

      // retrigger -- live-update if voice supports it
      if (!toggle && reg.has(voiceKey)) {
        const v = reg.get(voiceKey);
        // a tape played elsewhere, met again by the playhead: it starts
        // again from its start, not carry on (a live re-run still updates it)
        if (v?.external && cueElement) {
          try { v.stop(0.01); } catch (err) { console.warn("[AUDIO] voice stop failed:", err); }
          reg.delete(voiceKey);
        }
      }
      if (!toggle && reg.has(voiceKey)) {
        const v = reg.get(voiceKey);
        // azi: written now and not before (or the other way): a new voice
        if (v?.update && hasSpatial(params) === !!v.spat) {
          v.update({
            azi: params.azi, elev: params.elev, dist: params.dist,
            amp, speed, pan: panVal, fadeIn, fadeOut, loop, src, in: ast.in ?? params.in, out: ast.out ?? params.out, seg: ast.seg ?? params.seg,
            // the effects the line says now, their times in beats as on the first run
            filter: ast.filter ?? params.filter ?? null,
            delay: secsOf(ast.delay ?? params.delay ?? null, ["time"]),
            reverb: secsOf(ast.reverb ?? params.reverb ?? null, ["time", "pre", "predelay"]),
          });
          v.steer?.(follows);              // what it follows now: this run's
          return;
        }
        try { v?.stop?.(Math.min(fadeOut, 0.03)); } catch (err) { console.warn("[AUDIO] voice stop failed:", err); }
        reg.delete(voiceKey);
      }
    }

    let replaceStop = () => { };
    reg.set(voiceKey, { uid: voiceKey, filename, stop: (sec) => replaceStop(sec), _pending: true });

    window.dispatchEvent(new CustomEvent("oscilla:audio", {
      detail: { uid: voiceKey, file: filename, state: "play" }
    }));
    noteAudioEnd(key, 0);

    // fetch -- with buffer caching (picked folder -> project -> shared)
    const buf = await fetchAudioBuffer(ctx, filename);

    const effectiveDuration = buf.duration / (Math.abs(Number(speed)) || 1);

    // Resolve fade values (handles percentages, rand, plain numbers)
    fadeIn = Math.min(resolveFade(fadeIn, effectiveDuration, 0), effectiveDuration);
    fadeOut = Math.min(resolveFade(fadeOut, effectiveDuration, 0), effectiveDuration);

    // -------------------------------------------------------
    // WAVEFORM DISPLAY
    // Default: "self" (render into the cue element itself).
    // Use waveform:none to suppress.
    //
    // Mono mode: single cursor on the waveform handle.
    // Poly mode: per-voice coloured peak layer + sub-cursor,
    //   auto-removed when the voice ends.
    // -------------------------------------------------------
    const waveformParam = ast.waveform ?? params.waveform ?? "self";
    let waveformHandle = null;
    let isGuestOnHost = false;  // true when routed to another uid's waveform

    // Resolve the score SVG (needed for both live rect creation and waveform rendering)
    const scoreSvg = cueElement?.ownerSVGElement
                  || document.querySelector("#scoreInner svg")
                  || document.querySelector(".oscilla-score-inner svg")
                  || document.querySelector("svg");

    // If no cueElement (e.g. live console), create an ephemeral SVG rect
    // centered in the visible viewport -- but only when waveform targets "self".
    // When waveform targets another uid (e.g. waveform:v1), skip rect creation
    // and let renderWaveform find the existing element by id.
    let effectiveElement = cueElement;
    if (!effectiveElement && scoreSvg && waveformParam === "self") {
      effectiveElement = createLiveAudioRect(scoreSvg, key);
    }

    if (waveformParam !== "none" && waveformParam !== "0" && scoreSvg) {
      // Build header (always visible) and details (toggleable) separately
      const headerStr = buildAudioInfoHeader({
        audioObjectType: ast.audioObjectType ?? params.audioObjectType,
        audioObjectLabel: ast.audioObjectLabel ?? params.audioObjectLabel,
        filename,
        playbackClients: getPlaybackClientsFromEl(cueElement)
      });
      const detailsStr = buildAudioInfoDetails({
        filename, amp, speed, pan: panVal,
        loop, fadeIn, fadeOut,
        inPt: makeInSpec(ast.in ?? params.in ?? 0).first,
        outPt: Number(ast.out ?? params.out ?? 0),
        poly: polyActive ? polyNum : null
      });

      // When waveform targets another uid, reuse that existing handle
      // instead of creating a duplicate waveform group on the same element.
      // Mark as "guest" so this voice gets its own sub-cursor and doesn't
      // interfere with the host waveform's main cursor.
      if (waveformParam !== "self") {
        const existingTarget = getWaveform(waveformParam);
        if (existingTarget) {
          waveformHandle = existingTarget;
          isGuestOnHost = true;
          // Update info text to reflect the latest layer (respect visibility state)
          if (waveformHandle._detailsDiv) {
            waveformHandle._detailsDiv.textContent = detailsStr;
            // Ensure visibility respects the handle's state
            waveformHandle._detailsDiv.style.display = waveformHandle._labelVisible ? "" : "none";
            if (waveformHandle._labelVisible) updateWaveformLayout(waveformHandle);
          } else if (waveformHandle._infoText) {
            waveformHandle._infoText.textContent = detailsStr;
            updateWaveformLayout(waveformHandle);
          }
          // Add as a peak layer so the file is visible on the host waveform
          if (buf) {
            addPeakLayer(waveformHandle, buf, filename, { id: voiceKey });
          }
        }
      }

      // No existing handle to reuse -- create one
      if (!waveformHandle) {
        // Pick up waveform color from live rect (self or target)
        const wfColor = (params.color ?? ast.color) ?? effectiveElement?._waveformColor
          ?? ((waveformParam !== "self" && scoreSvg.getElementById(waveformParam)?._waveformColor)
          || undefined);

        waveformHandle = renderWaveform(scoreSvg, waveformParam, buf, key, filename, {
          element: effectiveElement,
          header: headerStr,
          info: detailsStr,
          color: wfColor
        });
      }
    }

    // If still no handle, reuse existing waveform by uid
    if (!waveformHandle && waveformParam !== "none" && waveformParam !== "0") {
      waveformHandle = getWaveform(key) || null;
    }

    // ---------------------------------------------------------
    // SUB-CURSORS: poly voices and guest voices (waveform:otherUid)
    //
    // Poly voices and guests both need independent sub-cursors so
    // they don't overwrite the host waveform's main cursor.
    // ---------------------------------------------------------
    let voiceCursor = null;
    const needsSubCursor = polyActive || ast._isSpreadVoice || isGuestOnHost;

    if (needsSubCursor && waveformHandle && buf) {
      if (polyActive || ast._isSpreadVoice) {
        addPeakLayer(waveformHandle, buf, filename, { id: voiceKey });
      }
      const layerColor = waveformHandle._peakLayers?.get(voiceKey)?.color || "#c00";

      if (ctx) {
        voiceCursor = addCursor(waveformHandle, voiceKey, {
          color: layerColor, width: "1.6", opacity: "0.55"
        });
      }
    }
    // -------------------------------------------------------

    // engine:external -- another program plays it; this draws it, the
    // cursor where that program reports it is (externalTape.js)
    if (isExternalEngine(ast, params)) {
      const own = () => reg.get(voiceKey);
      const inSec = Number(ast.in ?? params.in) || 0;
      const lock = cueElement ? scoreLock(cueElement, inSec, Number(speed) || 1) : null;
      // started by the playhead: from where the score is in it (a few ms in
      // on entering it, mid-file when playback began or jumped inside it)
      const joinAt = lock?.expected();
      const startSec = Math.min(Math.max(inSec, Number.isFinite(joinAt) ? joinAt : inSec), Math.max(0, buf.duration - 0.01));
      let voice = null;
      voice = startExternalTape({
        uid: voiceKey, src: filename, duration: buf.duration, handle: voiceCursor || waveformHandle,
        startSec,
        rate: Number(speed) || 1, loop: Number(loop) || 0, amp: Number(amp) || 0,
        latencyMs: Number(ast.latency ?? params.latency) || 0,
        follows,
        lock,
        onEnded: () => {
          if (own() !== voice) return;
          reg.delete(voiceKey);
          if (liveAudioRects.has(key) && ![...reg.keys()].some(k => k === key || k.startsWith(`${key}__`))) removeLiveAudioRect(key);
          window.dispatchEvent(new CustomEvent("oscilla:audio", { detail: { uid: voiceKey, file: filename, state: "stop" } }));
        },
      });
      voice._box = effectiveElement;
      reg.set(voiceKey, voice);
      replaceStop = (sec) => voice.stop(sec);
      return;
    }

    // Extract effect parameters from AST
    const effectParams = {
      filter: ast.filter ?? params.filter ?? null,
      // their times are beats (beats.js)
      delay: secsOf(ast.delay ?? params.delay ?? null, ["time"]),
      reverb: secsOf(ast.reverb ?? params.reverb ?? null, ["time", "pre", "predelay"])
    };

    // Per-object output bus (falls back to masterOutput if unset)
    const busIn = channelFor(key, getBus(ast.outputBus ?? params.outputBus));   // through its name's fader (level.js)
    // azi: places the sound among the speakers (pan: is then not needed),
    // else it goes as its own channels, mono made two (speakers.js)
    const spat = hasSpatial(params) ? spatialPanner(ctx) : null;
    const dryDest = spat ? spat.input : toBus(ctx, buf?.numberOfChannels);
    (spat ? spat.output : dryDest).connect(busIn);
    let spatUnbinds = spat ? bindSpatial(spat, params) : [];

    // graph
    const gainNode = ctx.createGain();
    let panNode = null;
    let effectsChain = null;
    // a tap for viz(a1, view:...) -- the box drawing this voice live;
    // an analyser only reads, nothing is heard through it
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    gainNode.connect(analyser);
    // the tremolo for amp:sinOsc(...), after the gain (which carries the fades)
    const trem = ampSpec ? ctx.createGain() : null;
    if (trem) gainNode.connect(trem);
    const head = trem || gainNode;

    if (panVal !== null && !isNaN(panVal) && !spat) {
      panNode = new StereoPannerNode(ctx, { pan: panVal });
      head.connect(panNode);
      effectsChain = wireEffectsChain(ctx, panNode, effectParams, dryDest);
    } else {
      effectsChain = wireEffectsChain(ctx, head, effectParams, dryDest);
    }
    const mods = [];
    if (panMod && panNode) mods.push(attachModulator(panNode.pan, panMod));
    if (trem) mods.push(attachModulator(trem.gain, ampSpec, { scale: 1 / ampTop }));

    // How long to wait before tearing down the FX chain after the source
    // finishes (or after the explicit-stop fadeout). Without this delay the
    // reverb tail and delay echoes get cut off when we disconnect nodes.
    let _tail = 0;
    if (effectParams.reverb) {
      _tail = Math.max(_tail, (effectsChain?.reverb?.time ?? 1.8) + 0.5);
    }
    if (effectParams.delay) {
      const t = Number(effectParams.delay.time) || 0.25;
      const fb = Math.min(0.99, Math.max(0, Number(effectParams.delay.fb) || 0.25));
      const iters = fb > 0 ? Math.ceil(Math.log(0.01) / Math.log(fb)) : 1;
      _tail = Math.max(_tail, t * Math.min(iters, 20));
    }
    const tailTimeMs = Math.max(50, Math.round(_tail * 1000));

    let remaining = (loop === 0 ? Infinity : Number(loop)) || 1;
    let stopped = false;
    let paused = false;
    let pauseOffset = 0;  // offset within current segment when paused
    let srcNode = null;

    // Mutable params -- playOne reads these, update() writes them
    const live = {
      amp: Number.isFinite(+amp) ? +amp : 1,   // allow 0; only fallback on NaN/undefined
      speed: Number(speed) || 1,
      // keep the pattern so each loop iteration can draw its own speed
      speedPattern: (rawSpeed && typeof rawSpeed === "object" && rawSpeed.type === "pattern") ? rawSpeed : null,
      speedKey: String(ast.uid ?? params.uid ?? ast.src ?? params.src ?? "audio"),
      pan: panVal,
      fadeIn,
      fadeOut,
      buf,              // swappable on file change
      filename,         // current filename for display
      in: 0,            // start offset in seconds (set from inSpec below)
      inGen: null,      // start-point generator when in: is a list/pattern
      out: 0,           // end point in seconds (0 = full file)
      seg: 0,           // length of each play from its start point (0 = unset)
      segGen: null,     // seg generator when seg: is a list/pattern
      audioObjectType: ast.audioObjectType ?? params.audioObjectType,
      audioObjectLabel: ast.audioObjectLabel ?? params.audioObjectLabel
    };
    {
      const inSpec = makeInSpec(ast.in ?? params.in ?? 0);
      live.inGen = inSpec.gen;
      live.in = inSpec.first;
      const segRaw = ast.seg ?? params.seg;
      if (segRaw !== undefined) {
        const segSpec = makeInSpec(segRaw);
        live.segGen = segSpec.gen;
        live.seg = segSpec.first;
      }
    }
    // Resolve 'out' -- 0 means full buffer
    if (ast.out !== undefined || params.out !== undefined) {
      live.out = Number(ast.out ?? params.out);
    }

    let unsteer = null;                // the release of what it follows
    // its stop said once: by stop() at once (a pool takes its cursor and
    // layer away before the fade is out), or by cleanup() at a natural end
    let stopAnnounced = false;
    const announceStop = () => {
      if (stopAnnounced) return;
      stopAnnounced = true;
      window.dispatchEvent(new CustomEvent("oscilla:audio", {
        detail: { uid: voiceKey, file: live.filename, state: "stop" }
      }));
    };
    const stop = (dur = live.fadeOut) => {
      stopped = true;
      paused = false;
      unsteer?.(); unsteer = null;
      const now = ctx.currentTime;

      // Reset cursor immediately (don't wait for fadeout to complete)
      if (voiceCursor) {
        resetCursor(voiceCursor);
      } else if (waveformHandle) {
        resetCursor(waveformHandle);
      }

      announceStop();

      try {
        gainNode.gain.cancelScheduledValues(now);
        gainNode.gain.setValueAtTime(gainNode.gain.value, now);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, now + dur);
      } catch (err) {
        console.warn("[AUDIO] param ramp failed:", err);
      }

      try { srcNode?.stop(now + dur + 0.01); } catch { /* source never started or already stopped */ }

      // Stop while paused: pause() already stopped and nulled srcNode, so no
      // onended will fire to run cleanup. Without this the voice stays in
      // activeAudioCues forever as an unresponsive zombie — clicks then
      // pause/stop/hot-update the corpse and the cue appears dead.
      if (!srcNode) {
        setTimeout(() => cleanup(), (dur + 0.02) * 1000);
      }
    };

    // Pause: store current offset and stop source immediately
    const pause = () => {
      if (paused || stopped) return false;

      const regVoice = reg.get(voiceKey);
      if (!regVoice?._startedAt) return false;

      const now = ctx.currentTime;
      const elapsed = now - regVoice._startedAt;
      const absSpeed = Math.abs(live.speed) || 1;
      pauseOffset = elapsed * absSpeed;  // offset in buffer time
      paused = true;

      // Stop source immediately (no fade)
      try { srcNode?.stop(now); } catch { /* source never started or already stopped */ }
      srcNode = null;

      // Stop cursor animation but keep cursor visible at current position
      const cursorHandle = voiceCursor || waveformHandle;
      if (cursorHandle) {
        cursorHandle._running = false;
        if (cursorHandle._rafId) {
          cancelAnimationFrame(cursorHandle._rafId);
          cursorHandle._rafId = null;
        }

        // Enable cursor dragging for scrubbing
        const inPoint = Math.max(0, live.in || 0);
        const endPoint = resolveEnd(live, inPoint);
        const segmentDuration = Math.max(0.01, endPoint - inPoint);

        enableCursorDrag(cursorHandle, (progress) => {
          // Convert progress (0-1) to offset in segment
          pauseOffset = progress * segmentDuration;
        });
      }

      return true;
    };

    // Resume: create new source starting from pauseOffset
    const resume = () => {
      if (!paused || stopped) return false;
      paused = false;

      // Disable cursor dragging
      const cursorHandle = voiceCursor || waveformHandle;
      if (cursorHandle) {
        disableCursorDrag(cursorHandle);
      }

      // playOneFromOffset will restart the cursor animation
      playOneFromOffset(pauseOffset);
      return true;
    };

    const update = (p) => {
      const now = ctx.currentTime;
      const ramp = 0.05; // 50ms smooth ramp

      // Immediate: amp
      if (p.amp !== undefined) {
        live.amp = Math.max(0, Math.min(2, Number(p.amp) || 0));     // to 2, as level() goes
        try {
          gainNode.gain.cancelScheduledValues(now);
          gainNode.gain.setValueAtTime(Math.max(0.0001, gainNode.gain.value), now);
          gainNode.gain.exponentialRampToValueAtTime(Math.max(0.0001, live.amp), now + ramp);
        } catch (err) {
          console.warn("[AUDIO] param ramp failed:", err);
        }
      }

      // azi:, elev:, dist: bound afresh
      if (spat && hasSpatial(p)) {
        for (const u of spatUnbinds) { try { u(); } catch { /* already gone */ } }
        spatUnbinds = bindSpatial(spat, p);
      }

      // Immediate: pan
      if (p.pan !== undefined && p.pan !== null && !spat) {
        live.pan = Math.max(-1, Math.min(1, Number(p.pan) || 0));
        if (panNode) {
          try {
            panNode.pan.cancelScheduledValues(now);
            panNode.pan.setValueAtTime(panNode.pan.value, now);
            panNode.pan.linearRampToValueAtTime(live.pan, now + ramp);
          } catch (err) {
            console.warn("[AUDIO] param ramp failed:", err);
          }
        } else {
          // a pan the voice was made without: the panner goes in after head,
          // before what head fed (the filter, or the bus and the delay and
          // reverb sends); the analyser and the effects stay where they were
          panNode = new StereoPannerNode(ctx, { pan: live.pan });
          const fed = effectsChain.filter ? [effectsChain.filter]
            : [effectsChain.dryDest, effectsChain.delay?.delay, effectsChain.reverb?.input].filter(Boolean);
          for (const n of fed) head.disconnect(n);
          head.connect(panNode);
          for (const n of fed) panNode.connect(n);
        }
      }

      // Immediate: filter parameters
      if (p.filter && effectsChain?.filter) {
        const f = effectsChain.filter;
        if (p.filter.freq !== undefined || p.filter.cutoff !== undefined) {
          const freq = evalMaybeRandom(p.filter.freq ?? p.filter.cutoff) ?? 1200;
          try {
            f.frequency.cancelScheduledValues(now);
            f.frequency.setValueAtTime(f.frequency.value, now);
            f.frequency.linearRampToValueAtTime(clamp(freq, 20, 20000), now + ramp);
          } catch (err) {
            console.warn("[AUDIO] param ramp failed:", err);
          }
        }
        if (p.filter.q !== undefined) {
          const q = evalMaybeRandom(p.filter.q) ?? 0.707;
          try {
            f.Q.cancelScheduledValues(now);
            f.Q.setValueAtTime(f.Q.value, now);
            f.Q.linearRampToValueAtTime(clamp(q, 0.0001, 30), now + ramp);
          } catch (err) {
            console.warn("[AUDIO] param ramp failed:", err);
          }
        }
      }

      // Immediate: delay parameters
      if (p.delay && effectsChain?.delay) {
        const d = effectsChain.delay;
        const mix = evalMaybeRandom(p.delay.mix);
        if (mix !== undefined && mix !== null) {
          const m = clamp(mix, 0, 1);
          try {
            d.dry.gain.linearRampToValueAtTime(1 - m, now + ramp);
            d.wet.gain.linearRampToValueAtTime(m, now + ramp);
          } catch (err) {
            console.warn("[AUDIO] param ramp failed:", err);
          }
        }
        if (p.delay.time !== undefined) {
          try {
            d.delay.delayTime.linearRampToValueAtTime(clamp(evalMaybeRandom(p.delay.time) ?? 0.25, 0, 5), now + ramp);
          } catch (err) {
            console.warn("[AUDIO] param ramp failed:", err);
          }
        }
        // the loop's fb (to 1.5), tone and sat, as a synth's (effects.js)
        const fb = evalMaybeRandom(p.delay.fb ?? p.delay.feedback);
        d.loop.set({ fb: fb ?? undefined, tone: p.delay.tone, sat: p.delay.sat }, now);
      }

      // Immediate: reverb -- the mix glides, a new time / damp / pre crossfades
      if (p.reverb && effectsChain?.reverb) updateReverb(effectsChain.reverb, p.reverb, ramp);

      // Speed: immediate ramp if direction unchanged, deferred on direction flip
      // Accept both "speed" and legacy "pitch"
      const newSpeed = p.speed ?? p.pitch;
      if (newSpeed !== undefined) {
        const oldSpeed = live.speed;
        live.speed = Number(newSpeed) || 1;
        const absSpeed = Math.abs(live.speed);
        const sameDirection = (oldSpeed > 0 && live.speed > 0) || (oldSpeed < 0 && live.speed < 0);

        if (sameDirection && srcNode) {
          // Same direction -- ramp abs rate immediately
          try {
            srcNode.playbackRate.cancelScheduledValues(now);
            srcNode.playbackRate.setValueAtTime(srcNode.playbackRate.value, now);
            srcNode.playbackRate.linearRampToValueAtTime(absSpeed, now + ramp);
          } catch (err) {
            console.warn("[AUDIO] param ramp failed:", err);
          }
        }
        // Direction change: takes effect on next loop (needs buffer swap)
      }

      // steered by a follow() (every frame): the sound only, not the label
      if (p._quiet) return;

      // Deferred: fades (applied on next loop iteration)
      // Compute current effective duration for fade resolution
      const curSegment = ((live.out > 0 && live.out <= live.buf.duration) ? live.out : live.buf.duration) - (live.in || 0);
      const curEffDur = curSegment / (Math.abs(live.speed) || 1);

      if (p.fadeIn !== undefined) live.fadeIn = resolveFade(p.fadeIn, curEffDur, 0);
      if (p.fadeOut !== undefined) live.fadeOut = resolveFade(p.fadeOut, curEffDur, 0);

      // Loop control: set remaining iterations
      // loop:0 = infinite, loop:1 = finish after current, loop:N = N more
      if (p.loop !== undefined) {
        const newLoop = Number(p.loop);
        if (newLoop === 0) {
          remaining = Infinity;
        } else {
          remaining = Math.max(1, newLoop);
        }
      }

      // Deferred: in/out points (applied on next loop iteration)
      if (p.in !== undefined) {
        const inSpec = makeInSpec(p.in);
        live.inGen = inSpec.gen;
        live.in = inSpec.first;
      }
      if (p.out !== undefined) live.out = Math.max(0, Number(p.out) || 0);
      if (p.seg !== undefined) {
        const segSpec = makeInSpec(p.seg);
        live.segGen = segSpec.gen;
        live.seg = segSpec.first;
      }

      // Deferred: file change (fetch+decode, swap on next loop)
      if (p.src !== undefined) {
        const newSrc = String(p.src);
        const newFilename = ensureAudioExtension(newSrc);
        if (newFilename !== live.filename) {
          // Async fetch -- don't block
          (async () => {
            try {
              const newBuf = await fetchAudioBuffer(ctx, newFilename);
              live.buf = newBuf;
              live.filename = newFilename;

              // Redraw waveform peaks for the new file
              if (waveformHandle) {
                updatePeaks(waveformHandle, newBuf, newFilename);
                resetCursor(waveformHandle);

                // Update details text, respecting visibility state
                if (waveformHandle._detailsDiv) {
                  const detailsStr = buildAudioInfoDetails({
                    filename: newFilename, amp: live.amp, speed: live.speed,
                    pan: live.pan, inPt: live.in, outPt: live.out
                  });
                  waveformHandle._detailsDiv.textContent = detailsStr;
                  waveformHandle._detailsDiv.style.display = waveformHandle._labelVisible ? "" : "none";
                  if (waveformHandle._labelVisible) updateWaveformLayout(waveformHandle);
                } else if (waveformHandle._infoText) {
                  waveformHandle._infoText.textContent = buildAudioInfoLabel({
                    filename: newFilename, amp: live.amp, speed: live.speed,
                    pan: live.pan, inPt: live.in, outPt: live.out,
                    audioObjectType: live.audioObjectType,
                    audioObjectLabel: live.audioObjectLabel
                  });
                  updateWaveformLayout(waveformHandle);
                }
              }
            } catch (err) {
              console.warn(`[audio] File change failed:`, err);
            }
          })();
        }
      }

      // Update waveform details text, respecting visibility state
      if (waveformHandle?._detailsDiv) {
        const detailsStr = buildAudioInfoDetails({
          filename: live.filename, amp: live.amp, loop: remaining,
          speed: live.speed, pan: live.pan,
          inPt: live.in, outPt: live.out,
          fadeIn: live.fadeIn, fadeOut: live.fadeOut
        });
        waveformHandle._detailsDiv.textContent = detailsStr;
        waveformHandle._detailsDiv.style.display = waveformHandle._labelVisible ? "" : "none";
        if (waveformHandle._labelVisible) updateWaveformLayout(waveformHandle);
      } else if (waveformHandle?._infoText) {
        waveformHandle._infoText.textContent = buildAudioInfoLabel({
          filename: live.filename, amp: live.amp, loop: remaining,
          speed: live.speed, pan: live.pan,
          inPt: live.in, outPt: live.out,
          fadeIn: live.fadeIn, fadeOut: live.fadeOut,
          audioObjectType: live.audioObjectType,
          audioObjectLabel: live.audioObjectLabel
        });
        updateWaveformLayout(waveformHandle);
      }
    };

    replaceStop = stop;
    const isPaused = () => paused;
    reg.set(voiceKey, {
      uid: voiceKey,
      filename,
      stop,
      pause,
      resume,
      isPaused,
      update: polyActive ? null : update,
      announcesStop: true,           // says its own stop: whoever stops it does not again
      // follow(): this voice steered by its sources. A poly voice is
      // steered too -- each one by itself, from where it started.
      steer: (f) => { unsteer?.(); unsteer = stopped ? null : steerFollows(f, v => update({ ...v, _quiet: true })); },
      gainNode,
      panNode,
      spat,
      srcNode,
      effectsChain,
      analyser,
      _box: ast._box ?? effectiveElement,      // a pool's or impulse's box (consoleBox), or its own
      _live: live
    });
    attachAudioView(voiceKey);
    reg.get(voiceKey).steer(follows);

    // Tear down the FX chain. Deferred via setTimeout so reverb/delay tails
    // can decay naturally. Idempotent — safe to schedule once per voice.
    const disconnectFxChain = () => {
      for (const m of mods) m.stop();
      try { trem?.disconnect(); } catch { /* already disconnected */ }
      try { gainNode.disconnect(); } catch { /* already disconnected */ }
      try { panNode?.disconnect(); } catch { /* already disconnected */ }
      for (const u of spatUnbinds) { try { u(); } catch { /* already gone */ } }
      spat?.dispose();
      try { dryDest.disconnect(); } catch { /* already disconnected */ }
      if (effectsChain) {
        try { effectsChain.filter?.disconnect(); } catch { /* already disconnected */ }
        try { effectsChain.delay?.dry?.disconnect(); } catch { /* already disconnected */ }
        try { effectsChain.delay?.wet?.disconnect(); } catch { /* already disconnected */ }
        try { effectsChain.delay?.delay?.disconnect(); } catch { /* already disconnected */ }
        try { effectsChain.delay?.fbGain?.disconnect(); } catch { /* already disconnected */ }
        disconnectReverb(effectsChain.reverb);
        try { effectsChain.sum?.disconnect(); } catch { /* already disconnected */ }
        try { effectsChain.dry?.disconnect(); } catch { /* already disconnected */ }
      }
      srcNode = null;
    };

    const cleanup = () => {
      // Check if this voice is still the current owner (not replaced by a new voice)
      const regEntry = reg.get(voiceKey);
      const isCurrentOwner = regEntry?.stop === stop;

      // Deactivate overlay instead of destroying (it stays for re-triggers)
      if (regEntry?._overlay?.el) {
        regEntry._overlay.el.classList.remove("is-active");
      }

      // Waveform cleanup: only reset cursor if we're still the owner
      // (prevents old voice from resetting cursor after new voice started)
      if (isCurrentOwner) {
        if (needsSubCursor && waveformHandle) {
          removeCursor(waveformHandle, voiceKey);
          removePeakLayer(waveformHandle, voiceKey);
        } else if (waveformHandle) {
          // Mono owner: reset the shared cursor
          resetCursor(waveformHandle);
        }
      }

      // Remove from active registry IMMEDIATELY so the audio object can be
      // re-triggered while the FX tail is still draining. The actual node
      // disconnect runs after tailTimeMs to keep reverb/delay alive.
      if (isCurrentOwner) reg.delete(voiceKey);

      // Remove ephemeral live rect if one was created for this voice
      // (e.g. when triggered without a cueElement).
      // For poly voices, only remove when no other voices with the same
      // base key are still active.
      // (Not a pool's or impulse's box: that stays between its hits.)
      if (isCurrentOwner && !ast._box && liveAudioRects.has(key)) {
        const hasOtherVoices = [...reg.keys()].some(
          k => k === key || k.startsWith(`${key}__`)
        );
        if (!hasOtherVoices) removeLiveAudioRect(key);
      }

      announceStop();

      setTimeout(disconnectFxChain, tailTimeMs);
    };

    // Resume playback from a specific offset within the segment (used after pause)
    const playOneFromOffset = (offsetInSegment) => {
      srcNode = ctx.createBufferSource();

      const curSpeed = live.speed;
      const curAmp = live.amp;
      const curFadeOut = live.fadeOut;
      const reverse = curSpeed < 0;
      const absSpeed = Math.abs(curSpeed) || 1;

      const activeBuf = reverse
        ? getReversedBuffer(ctx, live.buf, live.filename + "__rev")
        : live.buf;
      srcNode.buffer = activeBuf;

      const inPoint = Math.max(0, live.in || 0);
      const endPoint = resolveEnd(live, inPoint);
      const segmentDuration = Math.max(0.01, endPoint - inPoint);
      // where this play sits in the file, for the waveform cursor
      const cursorRange = { from: inPoint / live.buf.duration, to: endPoint / live.buf.duration };

      // Calculate remaining duration from pause point
      const remainingSegment = Math.max(0.01, segmentDuration - offsetInSegment);
      const remainingDuration = remainingSegment / absSpeed;

      // Start offset in buffer, accounting for pause position
      const baseOffset = reverse ? live.buf.duration - endPoint : inPoint;
      const startOffset = baseOffset + offsetInSegment;

      srcNode.playbackRate.value = absSpeed;

      srcNode.connect(gainNode);

      const now = ctx.currentTime;
      gainNode.gain.cancelScheduledValues(now);
      gainNode.gain.setValueAtTime(curAmp, now);

      // Schedule fadeOut if this is the last iteration
      if (curFadeOut > 0 && (remaining === 1 || live.seg > 0)) {
        const clampedFadeOut = Math.min(curFadeOut, remainingDuration);
        const fadeOutStartTime = now + remainingDuration - clampedFadeOut;
        if (fadeOutStartTime > now) {
          gainNode.gain.setValueAtTime(curAmp, fadeOutStartTime);
          gainNode.gain.exponentialRampToValueAtTime(0.0001, now + remainingDuration);
        }
      }

      srcNode.onended = () => {
        if (stopped) {
          // Cursor already reset by stop(), cleanup checks ownership.
          // Defer disconnect so reverb tail / delay echoes can decay
          // naturally rather than being clipped.
          cleanup();
          return;
        }
        if (paused) {
          // Keep cursor visible at current position when paused
          return;
        }
        remaining--;
        if (remaining > 0) {
          if (voiceCursor) resetCursor(voiceCursor);
          else if (waveformHandle) resetCursor(waveformHandle);
          pauseOffset = 0; // reset for next loop iteration
          return playOne(false);
        }
        noteAudioEnd(key, 1);   // played to its end (not stopped)
        cleanup();
      };

      srcNode.start(0, startOffset, remainingSegment);

      // Update timing metadata for cursor sync
      const regVoice = reg.get(voiceKey);
      if (regVoice) {
        // Adjust _startedAt so cursor position is correct
        regVoice._startedAt = ctx.currentTime - (offsetInSegment / absSpeed);
        regVoice._segmentDuration = segmentDuration;
        regVoice._playbackSpeed = curSpeed;
      }

      // Resume cursor from current position
      if (voiceCursor) {
        startCursor(voiceCursor, ctx, ctx.currentTime - (offsetInSegment / absSpeed), segmentDuration, live.speed, loop, cursorRange);
      } else if (waveformHandle) {
        startCursor(waveformHandle, ctx, ctx.currentTime - (offsetInSegment / absSpeed), segmentDuration, live.speed, loop, cursorRange);
      }
    };

    const playOne = (first) => {
      srcNode = ctx.createBufferSource();

      // Multiple start points: each loop iteration draws the next one
      // (the first play uses the start point drawn at voice creation)
      if (!first) { drawNextIn(live); drawNextSeg(live); drawNextSpeed(live); }

      // Read from live params (updated by live console)
      const curSpeed = live.speed;
      const curAmp = live.amp;
      // seg: grains get their fades on EVERY play (declick), not just first/last
      const curFadeIn = (first || live.seg > 0) ? live.fadeIn : 0;
      const curFadeOut = live.fadeOut;
      const reverse = curSpeed < 0;
      const absSpeed = Math.abs(curSpeed) || 1;

      // For reverse: use a reversed copy of the buffer, play with positive rate
      const activeBuf = reverse
        ? getReversedBuffer(ctx, live.buf, live.filename + "__rev")
        : live.buf;
      srcNode.buffer = activeBuf;

      // In/out points (relative to original buffer direction)
      const inPoint = Math.max(0, live.in || 0);
      const endPoint = resolveEnd(live, inPoint);
      const segmentDuration = Math.max(0.01, endPoint - inPoint);
      // where this play sits in the file, for the waveform cursor
      const cursorRange = { from: inPoint / live.buf.duration, to: endPoint / live.buf.duration };
      const iterDuration = segmentDuration / absSpeed;

      // For reversed buffer, flip in/out offsets
      const startOffset = reverse
        ? live.buf.duration - endPoint
        : inPoint;

      srcNode.playbackRate.value = absSpeed;

      srcNode.connect(gainNode);

      const now = ctx.currentTime;

      // Schedule the entire envelope upfront
      gainNode.gain.cancelScheduledValues(now);

      const safeAmp = Math.max(0.0001, curAmp);
      if (first && curFadeIn > 0) {
        gainNode.gain.setValueAtTime(0.0001, now);
        gainNode.gain.exponentialRampToValueAtTime(safeAmp, now + Math.min(curFadeIn, iterDuration));
      } else {
        gainNode.gain.setValueAtTime(safeAmp, now);
      }

      // Schedule fadeOut BEFORE the sound ends
      if (curFadeOut > 0 && (remaining === 1 || live.seg > 0)) {
        const clampedFadeOut = Math.min(curFadeOut, iterDuration);
        const fadeOutStartTime = now + iterDuration - clampedFadeOut;

        if (fadeOutStartTime > now + curFadeIn) {
          gainNode.gain.setValueAtTime(curAmp, fadeOutStartTime);
          gainNode.gain.exponentialRampToValueAtTime(0.0001, now + iterDuration);
        } else {
          gainNode.gain.exponentialRampToValueAtTime(0.0001, now + iterDuration);
        }
      }

      srcNode.onended = () => {
        if (stopped) {
          // Cursor already reset by stop(), cleanup checks ownership.
          // Defer disconnect so reverb tail / delay echoes can decay
          // naturally rather than being clipped.
          cleanup();
          return;
        }
        if (paused) {
          // Keep cursor visible at current position when paused
          return;
        }
        remaining--;
        if (remaining > 0) {
          if (voiceCursor) resetCursor(voiceCursor);
          else if (waveformHandle) resetCursor(waveformHandle);
          return playOne(false);
        }

        // Sound finished naturally - cleanup
        noteAudioEnd(key, 1);   // played to its end (not stopped)
        cleanup();
      };

      srcNode.start(0, startOffset, segmentDuration);

      // Store timing metadata on registry for external cursor sync
      // (HTML audio objects need this since they can't call startCursor directly)
      const regVoice = reg.get(voiceKey);
      if (regVoice) {
        regVoice._startedAt = ctx.currentTime;
        regVoice._segmentDuration = segmentDuration;
        regVoice._playbackSpeed = curSpeed;
      }

      // Start waveform cursor tracking
      // Waveform contour is always displayed normally (not mirrored).
      // Reverse playback is conveyed by the cursor moving right-to-left
      // (startCursor handles this via negative speed).
      // Pass loop count so cursor wraps for looping audio.
      if (voiceCursor) {
        startCursor(voiceCursor, ctx, ctx.currentTime, segmentDuration, live.speed, loop, cursorRange);
      } else if (waveformHandle) {
        startCursor(waveformHandle, ctx, ctx.currentTime, segmentDuration, live.speed, loop, cursorRange);
      }
    };

    // -------------------------------------------------------
    // OVERLAY SUPPORT
    // Suppressed when waveform is active (info shown as SVG text)
    // -------------------------------------------------------
    let overlay = null;
    if (cueElement && overlayFlag > 0 && !waveformHandle) {
      if (cueElement._audioOverlay) {
        overlay = cueElement._audioOverlay;
      } else {
        overlay = createAudioOverlay({
          anchorEl: cueElement,
          label: voiceKey,
          track: true
        });
      }

      if (overlay?.el) {
        overlay.el.classList.add("oscilla-audio-overlay", "is-active");
        const details = `amp:${live.amp} loop:${loop}${live.speed !== 1 ? ` speed:${live.speed}` : ""}`;
        overlay.update(`${filename} | ${details}`);
        overlay.position();
      }
    }

    // Store overlay and waveform references in registry for cleanup
    const regEntry = reg.get(voiceKey);
    if (regEntry) {
      regEntry._overlay = overlay;
      regEntry._cueElement = cueElement;
      if (waveformHandle) regEntry._waveform = waveformHandle;
    }

    playOne(true);

  } catch (err) {
    console.error("[AUDIO] handleAudioCue error:", err);

    const errKey = ast?.uid?.trim();
    const src = ast?.src;
    const file = src ? ensureAudioExtension(src) : undefined;
    if (errKey && activeAudioCues.has(errKey)) {
      activeAudioCues.delete(errKey);
      window.dispatchEvent(new CustomEvent("oscilla:audio", {
        detail: { uid: errKey, file, state: "stop" }
      }));
    }
  }
}

// =============================================================
//  stopAllAudio -- Global fade-out stop (filename scoped)
// =============================================================
export function stopAllAudio(filename, fadeOutSec = 1.0) {
  const reg = activeAudioCues;

  for (const [key, voice] of reg) {
    if (filename && voice.filename !== filename) continue;

    try { voice.stop?.(fadeOutSec); } catch (err) { console.warn("[AUDIO] voice stop failed:", err); }

    // out of the registry at once: a re-run during the fade makes a new voice
    reg.delete(key);

    if (!voice.announcesStop) window.dispatchEvent(new CustomEvent("oscilla:audio", {
      detail: { uid: key, file: voice.filename, state: "stop" }
    }));
  }
}

// =============================================================
//  Global stop button handler
// =============================================================
// a poly: spread's voices still to come, by sound: uid -> Set of timers
const spreadTimers = new Map();
function cancelSpread(uid = null) {
  for (const [key, timers] of [...spreadTimers]) {
    if (uid != null && key !== uid) continue;
    for (const t of timers) clearTimeout(t);
    spreadTimers.delete(key);
  }
}

// What else a hush(uid:...) stops, registered by the modules it would be a
// cycle to import (say.js: its phrases still being made). fn(uids). A bare
// hush() runs the "sound" reset instead (teardown.js onReset).
const hushHooks = new Set();
export const onHush = fn => { hushHooks.add(fn); };

/** Every voice on this client, now. What is still to make a sound -- say()'s
 *  phrases, a solo, the console's trig: lines -- goes with the "sound" reset,
 *  which runs this first (stopEverySound). */
export function killAllAudio() {
  cancelSpread();
  stopAllAudioImpulses();
  stopAllSynths();  // Stop all synth voices
  nukeSynthAudio();  // Nuclear option for zombie synths
  stopAllAudio(null, 0.15);
  clearLiveAudioRects();
}

// The panic button: the whole "sound" reset, as a bare hush(), so a trig:beat
// line or a phrase still being made does not start sound again
document.getElementById("stop-audio-button")?.addEventListener("click", () => runReset("sound"));

/** Every sound, and what a sound leaves set: bare hush(), and a project switch. */
export function stopEverySound() {
  killAllAudio();
  resetLevels();
  releaseHolds();
}
onReset("sound", stopEverySound);

/**
 * The sound named `uid`, whatever made it: a synth (with its poly voices),
 * an impulse process, an audio file or pool voices (keyed uid or uid__…).
 * `rel` is the release in seconds; null lets each kind use its own.
 */
export function stopSound(uid, rel = null) {
  uid = String(uid ?? "").trim();
  if (!uid) return;
  cancelSpread(uid);
  stopSynth(uid, rel);
  stopAudioImpulse(uid, rel ?? 0);
  const reg = activeAudioCues;
  for (const [key, voice] of [...reg]) {
    if (key !== uid && !key.startsWith(`${uid}__`)) continue;
    try { voice.stop?.(rel ?? 0.3); } catch (err) { console.warn("[AUDIO] voice stop failed:", err); }
    reg.delete(key);
    if (!voice.announcesStop) window.dispatchEvent(new CustomEvent("oscilla:audio", {
      detail: { uid: key, file: voice.filename, state: "stop" }
    }));
  }
  // Its box goes once the fade is over, unless a new voice has come in.
  // (A voice removes its own box when it ends only while it still holds
  // its place in the registry -- taken out above, it no longer does.)
  if (liveAudioRects.has(uid)) {
    setTimeout(() => {
      const playing = [...reg.keys()].some(k => k === uid || k.startsWith(`${uid}__`));
      if (!playing) removeLiveAudioRect(uid);
    }, ((rel ?? 0.3) + 0.15) * 1000);
  }
}

/**
 * hush() -- silence. Bare, everything (the stop button); with uid:, only
 * the sounds named, one or a list, released over rel: seconds.
 *
 *     hush()
 *     hush(uid:pad)
 *     hush(pad, 6)                 (fade over 6 beats: rel:6)
 *     hush(uid:[pad, rain], rel:3)
 */
export function handleHushCue(ast) {
  const arg = name => ast?.args?.find(a => a.type === name)?.value;
  const uids = arg("uid");
  const rel = secs(arg("rel") ?? arg("release") ?? null);     // beats (beats.js)
  if (uids == null) {
    runReset("sound");               // every module's own stop (teardown.js): stopEverySound, the console's trig: watchers, say()'s phrases ...
    return;
  }
  const hushed = [];
  for (const uid of [].concat(uids)) {
    // a shape cluster's name reaches the sounds of all its members
    const cluster = document.getElementById(String(uid));
    const members = cluster?.classList?.contains("oscilla-shape-cluster")
      ? [...cluster.querySelectorAll(".oscilla-shape")].map(m => m.id) : [];
    for (const u of [uid, ...members]) { stopSound(u, rel == null ? null : Number(rel)); hushed.push(u); }
  }
  // their faders (level()) back to 1 once they are quiet: the next is heard
  setTimeout(() => resetLevels(hushed), ((rel == null ? 0.3 : Number(rel)) + 0.3) * 1000);
  releaseHolds(hushed);           // hold(): the next of them plays as written
  for (const fn of hushHooks) fn(hushed);
}

// for the DevTools console (cue_audio.md)
window.clearLiveAudioRects = clearLiveAudioRects;

// =============================================================
//  Mute (suspend / resume the shared AudioContext)
//
// Muting suspends the AudioContext — every active voice keeps its
// internal state but produces no output, and any new sound played
// while muted is also silent until unmuted. This is independent of
// the panic button (which actually stops voices).
//
// `source` is "local" (button press) or "remote" (WS broadcast); used
// only for log clarity.
// =============================================================
let _oscillaMuted = false;
function _updateMuteButtonUI(muted) {
  const btn = document.getElementById("mute-button");
  if (!btn) return;
  btn.dataset.muted = muted ? "true" : "false";
  btn.classList.toggle("active", muted);
  btn.title = muted ? "Unmute audio (local)" : "Mute audio (local)";
  const onIcon  = btn.querySelector(".mute-icon-on");
  const offIcon = btn.querySelector(".mute-icon-off");
  if (onIcon)  onIcon.style.display  = muted ? "none" : "";
  if (offIcon) offIcon.style.display = muted ? "" : "none";
}
export function setOscillaMute(muted, source = "local") {
  const ctx = sharedAudioCtx;
  _oscillaMuted = !!muted;
  _updateMuteButtonUI(_oscillaMuted);
  if (!ctx) {
    return;
  }

  // Many code paths call ctx.resume() on every cue fire / user gesture
  // (handleAudioCue, synth start, metronome, screen unlock, audio
  // analysis, …). A simple ctx.suspend() is undone by the next of
  // those, which is why "I muted but I still hear audio" happens.
  //
  // Patch ctx.resume() once so it's a no-op while _oscillaMuted is
  // true; the original is preserved for genuine resume calls. The
  // patch stays in place permanently (it's transparent when unmuted).
  if (!ctx._origResume) {
    ctx._origResume = ctx.resume.bind(ctx);
    ctx.resume = function patchedResume() {
      if (_oscillaMuted) return Promise.resolve();
      return ctx._origResume();
    };
  }

  try {
    if (_oscillaMuted) {
      if (ctx.state !== "suspended") ctx.suspend();
    } else {
      // Use the original resume directly — the patched version would
      // refuse since the flag flip happens before the call.
      if (ctx.state !== "running") ctx._origResume();
    }
  } catch (err) {
    console.warn("[AUDIO] setOscillaMute failed:", err);
  }
}
window.isOscillaMuted = function isOscillaMuted() { return _oscillaMuted; };

document.getElementById("mute-button")?.addEventListener("click", () => {
  setOscillaMute(!_oscillaMuted, "local");
});

// =============================================================
//  Prime audio overlay (called during assignCues)
// =============================================================
export function primeAudioOverlay(ast, cueElement) {
  if (!ast || !cueElement) return;

  const params = ast.params || ast;
  const overlayLevel = parseOverlayLevel(params.overlay ?? ast.overlay);
  if (overlayLevel <= 0) return;

  if (cueElement._audioOverlayPrimed) return;
  cueElement._audioOverlayPrimed = true;

  const overlay = createAudioOverlay({
    anchorEl: cueElement,
    label: "audioFile",
    track: true
  });

  if (overlay?.el) {
    const text = formatAudioFileOverlay(params, overlayLevel);
    overlay.update(text);
    overlay.position();
    cueElement._audioOverlay = overlay;
    cueElement._audioOverlayLevel = overlayLevel;
  }
}

// =============================================================
//  Prime waveform display (called during assignCues)
//  Async -- fetches and decodes audio buffer, then renders
//  waveform into the score SVG so it is visible before playback.
// =============================================================
export async function primeWaveform(ast, cueElement) {
  if (!ast || !cueElement) return;

  const params = ast.params || ast;
  const waveformParam = ast.waveform ?? params.waveform ?? "self";

  if (waveformParam === "none" || waveformParam === "0") return;
  if (cueElement._waveformPrimed) return;
  cueElement._waveformPrimed = true;

  const src = ast.src ?? params.src;
  if (!src) return;

  const filename = ensureAudioExtension(src);
  const uid = ast.uid ?? params.uid ?? src;

  // Register task SYNCHRONOUSLY before any async work
  // This ensures hideLoader() knows about pending audio loads
  const taskId = `audio:${filename}`;
  const alreadyCached = audioBufferCache.has(filename) || window._audioTaskRegistered?.has(taskId);
  if (!alreadyCached) {
    if (!window._audioTaskRegistered) window._audioTaskRegistered = new Set();
    window._audioTaskRegistered.add(taskId);
    registerTask(taskId, filename);
  }

  try {
    const buf = await fetchAudioBuffer(sharedAudioCtx, filename);

    // Build header (always visible) and details (toggleable) separately
    const headerStr = buildAudioInfoHeader({
      audioObjectType: params.audioObjectType ?? ast.audioObjectType,
      audioObjectLabel: params.audioObjectLabel ?? ast.audioObjectLabel,
      filename,
      playbackClients: getPlaybackClientsFromEl(cueElement)
    });
    const detailsStr = buildAudioInfoDetails({
      filename,
      amp:    params.amp ?? ast.amp ?? 1,
      loop:   params.loop ?? ast.loop ?? 1,
      speed:  params.speed ?? params.pitch ?? ast.speed ?? ast.pitch ?? 1,
      pan:    params.pan ?? ast.pan,
      inPt:   makeInSpec(params.in ?? ast.in ?? 0).first,
      outPt:  Number(params.out ?? ast.out ?? 0),
      fadeIn:  params.fadeIn ?? ast.fadeIn ?? 0,
      fadeOut: params.fadeOut ?? ast.fadeOut ?? 0
    });

    const svg = cueElement.ownerSVGElement;
    if (svg) {
      // color: -- the waveform's (and its cursor's) colour
      const wfColor = params.color ?? ast.color;
      const wfHandle = renderWaveform(svg, waveformParam, buf, uid, filename, {
        element: cueElement,
        header: headerStr,
        info: detailsStr,
        ...(wfColor != null ? { color: String(wfColor) } : {})
      });

      if (wfHandle) {
        // Hide base contour -- use colored peak layer for consistency with pool/impulse
        if (wfHandle.upperLine) wfHandle.upperLine.setAttribute("opacity", "0");
        if (wfHandle.lowerLine) wfHandle.lowerLine.setAttribute("opacity", "0");

        // Add single file as preview layer (use filename as id for consistency with restoration)
        addPeakLayer(wfHandle, buf, filename, {
          id: `preview-${filename}`,
          opacity: "0.75",
          ...(wfColor != null ? { color: String(wfColor) } : {})
        });

        // Store preview data for restoration after all voices stop
        // (mirrors audioPool behavior so waveform stays visible)
        wfHandle._previewData = [{ filename, buf }];

        // If voices are still playing (re-render happened mid-playback), restart
        // the cursor now that the handle is available — the prime is async, so
        // the handle did not exist when the voice started its cursor.
        const cp = getCursorParams(uid);
        if (cp && !wfHandle._running) {
          startCursor(wfHandle, cp.audioCtx, cp.startTime, cp.duration, cp.speed, cp.loop, cp.range);
        }
      }
    }
  } catch (err) {
    console.warn(`[audio] Waveform prime failed for ${filename}:`, err);
  }
}
